import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { PrimaryButton as Button, Loading } from '@shape-ds/components';
import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ONE_MEGABYTE } from 'config/constants';
import { useGlobalNotification } from 'hooks';
import { useFileUploadMutation } from 'queries';
import { ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface UploadButtonProps {
  handleUploadFile?: (
    file: File,
    alarmId: string,
    fileUploadMutation: UseMutationResult<
      AxiosResponse<boolean, unknown>,
      unknown,
      { formData: unknown }
    >,
    onSuccessUpdateFile: (message: string) => void,
  ) => void;
  acceptFiles?: string;
  onSuccessUpdateFile: (message: string) => void;
  alarmId: string;
  isDeletingFile: boolean;
}

export const UploadButton = ({
  acceptFiles,
  alarmId,
  handleUploadFile,
  onSuccessUpdateFile,
  isDeletingFile,
}: UploadButtonProps) => {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const { displaySnackbar } = useGlobalNotification();
  const onError = () => {
    displaySnackbar({
      message: t('event_page:snackbarMessages.error.message'),
      type: 'error',
    });
  };
  const fileUploadMutation = useFileUploadMutation(onError);
  const acceptedFiles = acceptFiles || '.xls, .png, .txt, .pdf, .doc, .jpg';

  const isFileExtensionValid = (fileName: string, acceptedExtensions: string) => {
    const nameSplit = fileName.split('.');
    const extension = nameSplit[nameSplit.length - 1];

    if (extension && acceptedExtensions.includes(extension.toLowerCase())) return true;

    return false;
  };

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        hidden
        data-testid="file-input"
        accept={acceptedFiles}
        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];

          if (!isFileExtensionValid(file?.name || '', acceptedFiles)) {
            displaySnackbar({
              message: t('event_page:snackbarMessages.error.file_extension'),
              type: 'error',
            });

            return;
          }

          if (file && file.size <= 2 * ONE_MEGABYTE) {
            e.target.value = '';
            if (handleUploadFile)
              handleUploadFile(file, alarmId, fileUploadMutation, () =>
                onSuccessUpdateFile(file.name.trim()),
              );
          } else {
            displaySnackbar({
              message: t('event_page:snackbarMessages.error.file_size'),
              type: 'error',
            });
          }
        }}
      />
      <Button
        onClick={() => (fileInput?.current as HTMLInputElement)?.click()}
        startIcon={<AttachFileOutlinedIcon />}
        disabled={fileUploadMutation.isLoading || isDeletingFile}
      >
        {fileUploadMutation.isLoading ? <Loading /> : t('components:upload.attach_file')}
      </Button>
    </>
  );
};
